import { DOMUtil, HeightWidth, Point } from "@mcleod/core";
import { Component, Cursor } from "..";
import { Panel } from "../components/panel/Panel";

/**
 * MovablePanelManager exists to make any panel movable on the screen, while ensuring the panel cannot be dragged
 * completely out of view (thus making it unreachable with the mouse).  It's primary use is to facilitate movable
 * Dialogs, but it can be used for other types of panels.
 */
export class MovablePanelManager {
    private dragHandle: Component; // The component inside the panel that the user will grab to start the move
    private minDragHandleHeight: number; // The minimum height (in pixels) of the drag handle that must stay visible
    private minDragHandleWidth: number; // The minimum width (in pixels) of the drag handle that must stay visible
    private mouseDownLocation: Point;
    private panel: Panel; // The panel that should be movable around the screen
    private panelHeightWidth: HeightWidth;

    constructor(panel: Panel, dragHandle: Component, minDragHandleHeight: number, minDragHandleWidth: number) {
        this.panel = panel;
        this.dragHandle = dragHandle;
        this.minDragHandleHeight = minDragHandleHeight;
        this.minDragHandleWidth = minDragHandleWidth;
    }

    private get panelElement(): HTMLElement {
        return this.panel?._element;
    }

    resetPanelHeightWidth() {
        this.panel.ensureLoaded().then(() => {
            this.panel.addMountListener(() => {
                this.panelHeightWidth = DOMUtil.getElementHeightWidth(this.panelElement);
                this.panelElement.style.height = `${this.panelHeightWidth.height}px`;
                this.panelElement.style.width = `${this.panelHeightWidth.width}px`;
            });
        });
    }

    public makeMovable() {
        this.resetPanelHeightWidth();
        this.dragHandle._element.addEventListener("mousedown", event => {
            if (event.target instanceof Element && event.target.closest('[data-exclude-from-drag="true"]')) {
                return; // Don't initiate dragging
            }

            // Get current position before starting drag
            const rect = this.panelElement.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(this.panelElement);
            
            // Store current left/top positions
            const currentLeft = parseInt(computedStyle.left) || rect.left;
            const currentTop = parseInt(computedStyle.top) || rect.top;
            
            // Store mouse position relative to viewport
            this.mouseDownLocation = { 
                x: event.clientX - rect.left, 
                y: event.clientY - rect.top
            };
            
            // Set initial position explicitly to prevent jump when starting to drag
            this.panel.left = currentLeft;
            this.panel.top = currentTop;

            document.addEventListener("mousemove", this.mouseMoveHandler);
            document.addEventListener("mouseup", this.mouseUpHandler);
        });

        window.addEventListener('resize', this.resizeHandler);
        this.cleanupResizeListenerOnClose();
        this.dragHandle.cursor = Cursor.GRAB;
    }

    private mouseMoveHandler = (event: MouseEvent) => {
        this.panelElement.style.transform = "unset";

        // Calculate new position
        const newLeft = event.clientX - this.mouseDownLocation.x;
        const newTop = event.clientY - this.mouseDownLocation.y;
        
        // Get boundary constraints
        const { minLeft, maxLeft, minTop, maxTop } = this.calculateBoundaryConstraints();
        
        // Apply boundary constraints
        this.panel.left = Math.min(Math.max(newLeft, minLeft), maxLeft);
        this.panel.top = Math.min(Math.max(newTop, minTop), maxTop);
    }
        
    private mouseUpHandler = () => {
        document.removeEventListener("mousemove", this.mouseMoveHandler);
        document.removeEventListener("mouseup", this.mouseUpHandler);
    };
    
    private resizeHandler = () => {
        if (this.panelElement == null){
            return;
        }
        
        // Get current position
        const currentLeft = parseInt(this.panelElement.style.left) || 0;
        const currentTop = parseInt(this.panelElement.style.top) || 0;
        
        // Get boundary constraints (same calculation as in mouseMoveHandler)
        const { minLeft, maxLeft, minTop, maxTop } = this.calculateBoundaryConstraints();
        
        // Check and adjust if needed
        if (currentLeft < minLeft || currentLeft > maxLeft) {
            this.panel.left = Math.min(Math.max(currentLeft, minLeft), maxLeft);
        }
        
        if (currentTop < minTop || currentTop > maxTop) {
            this.panel.top = Math.min(Math.max(currentTop, minTop), maxTop);
        }
    };
    
    private cleanupResizeListenerOnClose = () => {
        // Clean up the resize listener when dialog closes
        this.panel.addUnmountListener(() => {
            window.removeEventListener('resize', this.resizeHandler);
        });
    }

    /**
     * Calculates boundary constraints to ensure the drag handle remains partially visible
     */
    private calculateBoundaryConstraints(): { minLeft: number; maxLeft: number; minTop: number; maxTop: number; } {
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Get drag handle dimensions and position
        const handleRect = this.dragHandle._element.getBoundingClientRect();
        const panelRect = this.panelElement.getBoundingClientRect();
        
        // Calculate drag handle position relative to panel
        const handleRelativeLeft = handleRect.left - panelRect.left;
        const handleRelativeTop = handleRect.top - panelRect.top;
        const handleWidth = handleRect.width;
        const handleHeight = handleRect.height;
        
        // Calculate boundary constraints
        // 1. Left boundary: Don't allow panel to go further left than (handleRelativeLeft + handleWidth - minVisibleWidth)
        const minLeft = -(handleRelativeLeft + handleWidth - this.minDragHandleWidth);
        
        // 2. Right boundary: Don't allow panel to go further right than (viewportWidth - handleRelativeLeft - minVisibleWidth)
        const maxLeft = viewportWidth - (handleRelativeLeft + this.minDragHandleWidth);
        
        // 3. Top boundary: Don't allow panel to go further up than specified handle height
        const minTop = -(handleRelativeTop + handleHeight - this.minDragHandleHeight);
        
        // 4. Bottom boundary: Don't allow panel to go further down than specified handle height from top
        const maxTop = viewportHeight - (handleRelativeTop + this.minDragHandleHeight);
        
        return { minLeft, maxLeft, minTop, maxTop };
    }
}
